import { t } from "@redotech/redo-i18n";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { aiReturnReasonsFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  AiReturnReasonsState,
  isAiReturnReasonsStepValid,
  isSingleBranchAiReasonsFlowType,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  aiReasonsStepTypeName,
  STEP_TYPE_NAMES,
} from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import QuestionIcon from "@redotech/redo-web/icon-old/question.svg";
import { produce } from "immer";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

export const AI_RETURN_REASONS_STEP_TYPE_NAME =
  STEP_TYPE_NAMES[ModelStep.AI_RETURN_REASONS];

/** Every display title the AI reasons step can carry, across flow types. */
const AI_REASONS_STEP_TYPE_NAMES = new Set([
  AI_RETURN_REASONS_STEP_TYPE_NAME,
  aiReasonsStepTypeName(FlowType.WARRANTY),
]);

export function isAiReasonsStepTitle(title: string | undefined): boolean {
  return title !== undefined && AI_REASONS_STEP_TYPE_NAMES.has(title);
}

export interface AiReturnReasonsStepOptions {
  title?: string;
  /**
   * Single-branch mode (warranty flows): only the default route exists, so
   * the canvas exposes one handle and the details panel shows no built-in or
   * custom guidance routes.
   */
  singleBranch?: boolean;
}

/** Step options for the flow type the editor is showing. */
export function aiReturnReasonsStepOptions(
  flowType: FlowType | undefined,
): AiReturnReasonsStepOptions {
  return {
    title: aiReasonsStepTypeName(flowType),
    singleBranch: isSingleBranchAiReasonsFlowType(flowType),
  };
}

export const AI_RETURN_REASONS = (
  Details: React.ComponentType<StepTypeDetailsProps<AiReturnReasonsState>>,
  {
    title = AI_RETURN_REASONS_STEP_TYPE_NAME,
    singleBranch = false,
  }: AiReturnReasonsStepOptions = {},
): StepType<AiReturnReasonsState, ModelStep.AiReturnReasons> => ({
  Details: (props: StepTypeDetailsProps<AiReturnReasonsState>) =>
    React.createElement(Details, props),
  title,
  valid(state, flowType) {
    return isAiReturnReasonsStepValid(state, flowType).valid;
  },
  Icon: QuestionIcon,
  description() {
    return singleBranch
      ? t("AI-powered warranty reasons")
      : t("AI-powered return reasons");
  },
  customTitle() {
    return undefined;
  },
  downstream(state) {
    if (singleBranch) {
      return [{ id: state.next, label: t("Next") }];
    }
    // Order matches the merchant right-panel: damaged, wrong product, custom
    // guidance entries (in array order), then default ("All other cases go...").
    // The position in this array IS the handle index used by updateConnection
    // and the canvas's drag-to-connect interaction, so the two must stay aligned.
    return [
      { id: state.nextDamaged, label: t("Damaged") },
      { id: state.nextWrongProduct, label: t("Wrong Product") },
      ...(state.customGuidance ?? []).map((g) => ({
        id: g.next,
        label: g.description || t("Guidance"),
      })),
      { id: state.next, label: t("Default") },
    ];
  },
  maxDownstream(state) {
    return singleBranch ? 1 : 3 + (state.customGuidance?.length ?? 0);
  },
  empty: {
    next: undefined,
    nextWrongProduct: undefined,
    nextDamaged: undefined,
    nextExchange: undefined,
    customGuidance: [],
    additionalAiInfo: undefined,
    maxQuestions: undefined,
    merchantGuidance: undefined,
    customQuestion: undefined,
    customExplanationSubtext: undefined,
  },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
      if (state.nextWrongProduct === stepId) {
        state.nextWrongProduct = undefined;
      }
      if (state.nextDamaged === stepId) {
        state.nextDamaged = undefined;
      }
      if (state.customGuidance) {
        for (const g of state.customGuidance) {
          if (g.next === stepId) {
            g.next = undefined;
          }
        }
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (singleBranch) {
      if (handleIndex !== 0) {
        throw new Error(
          `Invalid handle index ${handleIndex} for single-branch AI reasons step`,
        );
      }
      return { ...state, next: targetStepId };
    }
    // Handle indices match downstream() order: 0=damaged, 1=wrongProduct,
    // 2..N+1=customGuidance[0..N-1], N+2=default.
    return produce(state, (draft) => {
      const customCount = draft.customGuidance?.length ?? 0;
      if (handleIndex === 0) {
        draft.nextDamaged = targetStepId;
      } else if (handleIndex === 1) {
        draft.nextWrongProduct = targetStepId;
      } else if (handleIndex < 2 + customCount) {
        const customIndex = handleIndex - 2;
        const guidance = draft.customGuidance?.[customIndex];
        if (!guidance) {
          throw new Error(
            `Invalid handle index ${handleIndex} for AI Return Reasons step`,
          );
        }
        guidance.next = targetStepId;
      } else if (handleIndex === 2 + customCount) {
        draft.next = targetStepId;
      } else {
        throw new Error(
          `Invalid handle index ${handleIndex} for AI Return Reasons step`,
        );
      }
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
      if (draft.nextWrongProduct === targetStepId) {
        draft.nextWrongProduct = undefined;
      }
      if (draft.nextDamaged === targetStepId) {
        draft.nextDamaged = undefined;
      }
      if (draft.customGuidance) {
        for (const g of draft.customGuidance) {
          if (g.next === targetStepId) {
            g.next = undefined;
          }
        }
      }
    });
  },
  toModel(state, id) {
    return {
      stepUUID: state.stepUUID,
      type: ModelStep.AI_RETURN_REASONS,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      next: id(state.next!),
      nextWrongProduct:
        state.nextWrongProduct !== undefined
          ? id(state.nextWrongProduct)
          : undefined,
      nextDamaged:
        state.nextDamaged !== undefined ? id(state.nextDamaged) : undefined,
      // nextExchange is deprecated — never written by new saves.
      customGuidance: state.customGuidance?.length
        ? state.customGuidance.map((g) => ({
            id: g.id,
            description: g.description,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            next: id(g.next!),
          }))
        : undefined,
      additionalAiInfo: state.additionalAiInfo,
      maxQuestions: state.maxQuestions,
      merchantGuidance: state.merchantGuidance,
      customQuestion: state.customQuestion,
      customExplanationSubtext: state.customExplanationSubtext,
    };
  },
  fromModel(model, id) {
    return aiReturnReasonsFromModel(model, id);
  },
});

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  type AiReturnReasonsState,
  type CustomGuidanceState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
